/**
 * Booking state persistence utilities
 * Ensures form data is preserved when going Back
 *
 * Where the draft lives:
 *
 * - **Web:** localStorage, exactly as before.
 * - **App (M4, mobile assessment §G M4, §R.66, §R.75, §R.76):** the secure
 *   store, because the draft carries the client's address, coordinates,
 *   instructions and mobile-money number for up to 24 hours. That store is
 *   async, and every function here is synchronous and called from render-phase
 *   initialisers, so the app reads an in-memory copy instead. The copy is
 *   loaded by `hydrateBookingDraft()` before the booking route can mount (the
 *   route's lazy loader awaits it in App.tsx), every write updates it at once,
 *   and the secure store is written behind it. Reading the secure store
 *   directly would hand the initialiser `null`, or a Promise, and the client's
 *   half-finished booking would reset on resume.
 *
 * Because the secure write lands later, anything that leaves the flow for
 * another screen (sign-in, ID verification) waits for it first through
 * `whenBookingDraftDurable()`: on Android, sign-in can background the WebView
 * and the process can be killed with the write still in flight.
 */

import type { BookingData } from '@/pages/BookingFlow';
import { IS_NATIVE, platform } from '@/platform';
import { logger } from '@/lib/logger';

const STORAGE_KEY = 'booking_draft';
const STORAGE_NOT_BEFORE_KEY = 'booking_draft_not_before';
const STORAGE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

interface StoredBookingData {
  /** Absent when the record only keeps other tabs' operation ids. */
  data?: BookingData;
  timestamp: number;
  /** Which step the client had reached, so signing in mid-flow resumes there. */
  step?: number;
  /**
   * The operation id of a submit that may already have reached the server,
   * per booking flow (bookingFlowId): two tabs sending two bookings at once
   * never share one (#1128 review).
   */
  submissions?: Record<string, BookingSubmission>;
  /** The single, unscoped form stored before per-flow ids. Read, never written. */
  submission?: BookingSubmission;
}

/**
 * M10: the id `create_booking` replays on, and the account it was minted for.
 * It is stored with the draft BEFORE the first request goes out and kept until
 * a booking comes back: a retry after a lost response, or after the app was
 * killed mid-submit, first looks the id up (bookingSubmission.ts) and gets the
 * booking that request made, however the draft, the clock or the price have
 * moved since. It is never handed to another account (#1119 review).
 *
 * Ids stored before the account was recorded (`userId` absent) go to whoever
 * submits next; the server scopes every operation to the caller anyway.
 */
interface BookingSubmission {
  operationId: string;
  userId?: string;
}

const FLOW_KEY = 'booking_flow_id';
let pageFlowId: string | null = null;

/**
 * Which booking flow this is, fixed for the page's lifetime: one in the app,
 * which has a single flow and must keep it across a restart; in a browser,
 * one per tab. A tab keeps its flow across a reload (sessionStorage). Any
 * other page load starts a new one: a duplicated tab, or one opened from
 * this one, begins with a COPY of this tab's sessionStorage and must not
 * share its operation (#1128 review). Losing the flow on a plain navigation
 * only costs the automatic recovery; the review step's overlap warning
 * still stands in front of a duplicate.
 */
function bookingFlowId(): string {
  if (IS_NATIVE) return 'app';
  if (pageFlowId) return pageFlowId;
  let id: string | null = null;
  try {
    const nav = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
    if (nav?.type === 'reload') id = sessionStorage.getItem(FLOW_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(FLOW_KEY, id);
    }
  } catch {
    id = id ?? crypto.randomUUID();
  }
  return (pageFlowId = id);
}

// ── The app's copy (native only) ────────────────────────────────────────────

/** How long boot waits for the secure store before going on without a draft. */
const HYDRATE_TIMEOUT_MS = 3000;

let cache: string | null = null;
let hydration: Promise<void> | null = null;
// Bumped by every write, so a hydration read that started before a write
// cannot land after it and restore the older draft.
let generation = 0;
// The newest value not yet handed to the secure store (`null` = remove). A
// worker writes only the newest, so typing in the instructions field does not
// queue one Keystore write per keystroke.
let queued: { value: string | null } | null = null;
let worker: Promise<void> | null = null;

function writeBehind(value: string | null): void {
  queued = { value };
  if (worker) return;
  // Cleared in `finally`, which always runs after this assignment, even when
  // the store throws before its first await. Clearing it inside the loop could
  // run first and leave a settled promise here forever, and flushBookingDraft
  // would then spin on it.
  worker = drainWrites().finally(() => {
    worker = null;
  });
}

async function drainWrites(): Promise<void> {
  while (queued) {
    const next = queued.value;
    queued = null;
    try {
      if (next === null) await platform.storage.secure.remove(STORAGE_KEY);
      else await platform.storage.secure.set(STORAGE_KEY, next);
    } catch (error) {
      logger.warn('Failed to write the booking draft to the secure store', error);
    }
  }
}

function readRaw(): string | null {
  if (IS_NATIVE) return cache;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const notBefore = Number(localStorage.getItem(STORAGE_NOT_BEFORE_KEY));
    const timestamp = Number((JSON.parse(raw) as Partial<StoredBookingData>).timestamp);
    if (Number.isFinite(notBefore) && notBefore > 0 && (!Number.isFinite(timestamp) || timestamp <= notBefore)) {
      return null;
    }
  } catch {
    // Let the caller's existing parser handle malformed legacy data.
  }
  return raw;
}

function writeRaw(value: string): void {
  if (!IS_NATIVE) {
    localStorage.setItem(STORAGE_KEY, value);
    return;
  }
  generation += 1;
  cache = value;
  writeBehind(value);
}

function removeRaw(): void {
  if (!IS_NATIVE) {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_NOT_BEFORE_KEY);
    return;
  }
  generation += 1;
  cache = null;
  writeBehind(null);
}

/**
 * Make drafts written before a passive session end unreadable without racing
 * a replacement account's newer draft. Used only when cross-tab Web Locks are
 * unavailable: deleting `booking_draft` after a replacement check can erase a
 * new tab's write, while a timestamp fence rejects only the ended session's
 * already-existing address and notes.
 */
export function quarantineBookingDraftBefore(timestamp: number): void {
  if (IS_NATIVE) return;
  try {
    const existing = Number(localStorage.getItem(STORAGE_NOT_BEFORE_KEY));
    localStorage.setItem(STORAGE_NOT_BEFORE_KEY, String(Math.max(Number.isFinite(existing) ? existing : 0, timestamp)));
  } catch (error) {
    logger.warn('Failed to quarantine the ended session booking draft', error);
  }
}

/**
 * Loads the app's copy of the draft from the secure store. Idempotent; on the
 * web it resolves at once. Never rejects: an unreadable store, or one that does
 * not answer within 3 seconds, leaves the app without a draft rather than
 * without a booking flow.
 *
 * A draft left in localStorage by an app build from before M4 moves to the
 * secure store here, and leaves localStorage either way.
 */
export function hydrateBookingDraft(): Promise<void> {
  if (!IS_NATIVE) return Promise.resolve();
  return (hydration ??= (async () => {
    const startedAt = generation;
    let stored: string | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      stored = await Promise.race([
        platform.storage.secure.get(STORAGE_KEY),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error('timed out')), HYDRATE_TIMEOUT_MS);
        }),
      ]);
    } catch (error) {
      logger.warn('Could not read the booking draft from the secure store', error);
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
    let legacy: string | null = null;
    try {
      legacy = localStorage.getItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to move.
    }
    // A write happened meanwhile: it is newer than anything read here.
    if (generation !== startedAt) return;
    cache = stored ?? legacy;
    if (stored === null && legacy !== null) writeBehind(legacy);
  })());
}

/**
 * Resolves once every draft write has reached the secure store (at once on
 * the web, where localStorage writes are synchronous). Never rejects: a failed
 * write is logged, and the caller goes on either way.
 */
export async function flushBookingDraft(): Promise<void> {
  if (!IS_NATIVE) return;
  while (worker) await worker;
  try {
    await platform.storage.flush();
  } catch (error) {
    logger.warn('Failed to flush the secure store', error);
  }
}

/**
 * Runs `then` once the draft is durable: synchronously on the web, after
 * `flushBookingDraft()` in the app. For every place the flow navigates to
 * another screen and expects to come back to the draft (§R.76).
 */
export function whenBookingDraftDurable(then: () => void): void {
  if (!IS_NATIVE) {
    then();
    return;
  }
  void flushBookingDraft().then(then);
}

/** Test seam: forget the app's copy and every pending write. */
export function resetBookingDraftForTests(): void {
  pageFlowId = null;
  cache = null;
  hydration = null;
  generation = 0;
  queued = null;
  worker = null;
}

// ── The draft ───────────────────────────────────────────────────────────────

/**
 * Save booking data to localStorage.
 *
 * `step` is optional so existing call sites keep working; when supplied it is
 * what lets an unauthenticated client bounce out to /auth at the payment gate
 * and return to the same step instead of restarting at step 1.
 */
export function saveBookingDraft(data: BookingData, step?: number): void {
  try {
    const stored: StoredBookingData = {
      data,
      timestamp: Date.now(),
      step: step ?? loadBookingDraftStep() ?? undefined,
      // Kept across edits until a booking comes back (see BookingSubmission).
      submissions: readStoredDraft()?.submissions,
      submission: readStoredDraft()?.submission,
    };
    writeRaw(JSON.stringify(stored));
  } catch (error) {
    console.error('Failed to save booking draft:', error);
  }
}

/**
 * The step stored alongside the draft, or null when there is none / it expired.
 */
export function loadBookingDraftStep(): number | null {
  try {
    const stored = readRaw();
    if (!stored) return null;
    const parsed: StoredBookingData = JSON.parse(stored);
    if (Date.now() - parsed.timestamp > STORAGE_EXPIRY_MS) return null;
    // Operation ids kept for other tabs, with no draft, are not a draft.
    if (!parsed.data) return null;
    return typeof parsed.step === 'number' ? parsed.step : null;
  } catch {
    return null;
  }
}

/**
 * When the draft was last written, or null when there is none / it expired.
 *
 * Read this in a render-phase initialiser, never in an effect: BookingFlow
 * re-saves the draft in a mount effect, so by the time effects run the
 * timestamp is always "just now" and the resume banner (#757 N6) would tell
 * every client their work was saved 0 minutes ago.
 */
export function loadBookingDraftSavedAt(): number | null {
  try {
    const stored = readRaw();
    if (!stored) return null;
    const parsed: StoredBookingData = JSON.parse(stored);
    if (typeof parsed.timestamp !== 'number') return null;
    if (Date.now() - parsed.timestamp > STORAGE_EXPIRY_MS) return null;
    if (!parsed.data) return null;
    return parsed.timestamp;
  } catch {
    return null;
  }
}

/**
 * Load booking data from localStorage
 */
export function loadBookingDraft(): BookingData | null {
  try {
    const stored = readRaw();
    if (!stored) return null;

    const parsed: StoredBookingData = JSON.parse(stored);

    // Check if expired
    if (Date.now() - parsed.timestamp > STORAGE_EXPIRY_MS) {
      clearBookingDraft();
      return null;
    }

    return parsed.data ?? null;
  } catch (error) {
    console.error('Failed to load booking draft:', error);
    return null;
  }
}

function readStoredDraft(): StoredBookingData | null {
  try {
    const stored = readRaw();
    if (!stored) return null;
    const parsed: StoredBookingData = JSON.parse(stored);
    if (Date.now() - parsed.timestamp > STORAGE_EXPIRY_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * The operation id this flow may already have sent for this account, or null.
 * An id minted for another account, or in another tab, is never returned.
 */
export function storedBookingOperation(userId: string): string | null {
  const stored = readStoredDraft();
  const submission = stored?.submissions?.[bookingFlowId()] ?? stored?.submission;
  if (!submission?.operationId) return null;
  if (submission.userId !== undefined && submission.userId !== userId) return null;
  return submission.operationId;
}

/**
 * The operation id for this account's next submit: the stored one when it
 * belongs to this account, otherwise a new one, stored with the draft before
 * it is returned. In the app, `flushBookingDraft()` then makes it durable
 * before the request goes out.
 *
 * It does not change when the booking is edited. An id that has not created
 * a booking can carry any request; one that has is found by the lookup before
 * a new request is built, or answers KB001 and is looked up then.
 */
export function bookingOperationFor(userId: string, data: BookingData): string {
  const operationId = storedBookingOperation(userId) ?? crypto.randomUUID();
  try {
    const stored = readStoredDraft();
    const next: StoredBookingData = {
      data,
      timestamp: Date.now(),
      step: stored?.step,
      // Other tabs' operations stay; an unscoped one becomes this flow's.
      submissions: { ...stored?.submissions, [bookingFlowId()]: { operationId, userId } },
    };
    writeRaw(JSON.stringify(next));
  } catch (error) {
    // The submit still goes out; only a retry after a reload loses the id.
    logger.warn('Failed to store the booking operation id', error);
  }
  return operationId;
}

/**
 * Forget this flow's operation id, for a journey that deliberately starts a
 * NEW booking (a `?task=` entry): it must not find, and show, the booking an
 * earlier send made (#1128 review). Other tabs' ids stay. A booking that
 * earlier send did make is still caught by the review step's overlap warning.
 */
export function forgetBookingFlowOperation(): void {
  const stored = readStoredDraft();
  if (!stored) return;
  const submissions = { ...stored.submissions };
  delete submissions[bookingFlowId()];
  try {
    writeRaw(JSON.stringify({ ...stored, submissions, submission: undefined }));
  } catch (error) {
    logger.warn('Failed to forget the booking operation id', error);
  }
}

/**
 * Clear the booking draft, and this flow's operation id with it. Another
 * tab's operation id stays, without any draft, so that tab can still look up
 * a booking whose answer it lost (#1128 review). In the app, the one flow is
 * the whole record.
 */
export function clearBookingDraft(): void {
  try {
    const others = { ...readStoredDraft()?.submissions };
    delete others[bookingFlowId()];
    if (Object.keys(others).length === 0) {
      removeRaw();
      return;
    }
    writeRaw(JSON.stringify({ timestamp: Date.now(), submissions: others }));
  } catch (error) {
    console.error('Failed to clear booking draft:', error);
  }
}

/**
 * Remove the whole record: the draft, and EVERY flow's operation id with it.
 * For a session end, where nothing in the record belongs to whoever uses this
 * browser next. clearBookingDraft() keeps other tabs' operation ids (each
 * holds the signed-out account's user id) so those tabs can still recover a
 * booking; after a sign-out they cannot, and should not, so this does not.
 * Never throws.
 */
export function purgeBookingDraft(): void {
  try {
    removeRaw();
  } catch (error) {
    logger.warn('Failed to remove the booking draft', error);
  }
}
